import type React from 'react'
import { useLayoutEffect, useRef, useState } from 'react'

import type { ShapeData } from '@/lib/shapes/types/shape-data'

import { computePhotoMainToolbarPos } from '../helpers/photo-main-toolbar-position'

/** Used until the toolbar has rendered once and its real width can be measured. */
const TOOLBAR_FALLBACK_WIDTH = 460

/**
 * The toolbar's position, measured against its own rendered width: recomputed before paint, once
 * more on the next frame (after the first measurement), on window scroll/resize, and whenever the
 * toolbar itself resizes. Returns the ref to put on the toolbar element.
 */
export function usePhotoMainToolbarPosition(
  canvasRef: React.RefObject<HTMLDivElement | null>,
  shape: ShapeData,
  scale: number
) {
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)

  useLayoutEffect(() => {
    const update = () => {
      const el = canvasRef.current

      if (!el) return

      const toolbarWidth = toolbarRef.current?.offsetWidth ?? TOOLBAR_FALLBACK_WIDTH

      setPos(computePhotoMainToolbarPos(shape, scale, el.getBoundingClientRect(), toolbarWidth))
    }

    update()
    const raf = requestAnimationFrame(update)

    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => update()) : null

    if (observer && toolbarRef.current) observer.observe(toolbarRef.current)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
      observer?.disconnect()
    }
  }, [canvasRef, shape, scale])

  return { toolbarRef, pos }
}
